import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { message } from "antd";
import customParseFormat from "dayjs/plugin/customParseFormat";
import some from "lodash/some";
import get from "lodash/get";
import map from "lodash/map";
import isUndefined from "lodash/isUndefined";
import {
  SHIFT_CONFIG_COPY_SUCCESS,
  SHIFT_CONFIG_PASTE_FAILED,
} from "../constants/message";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

function parseTimeWithBaseDate(timeStr, baseDate) {
  if (timeStr.includes("T")) {
    return new Date(timeStr);
  }

  const date = new Date(baseDate);

  const [hours, minutes] = timeStr.split(":").map(Number);
  date.setHours(hours, minutes, 0, 0);

  return date;
}

export function analyzeBreaksInDuration(breakTimesArray, checkDurationObject) {
  const { start_time: rangeStartStr, end_time: rangeEndStr } =
    checkDurationObject;

  if (!rangeStartStr || !rangeEndStr) {
    return { breaksFound: [], totalOverlappingBreakTimeMinutes: 0 };
  }

  if (!Array.isArray(breakTimesArray)) {
    return { breaksFound: [], totalOverlappingBreakTimeMinutes: 0 };
  }

  const now = new Date();

  const rangeStart = parseTimeWithBaseDate(rangeStartStr, now);

  const rangeEnd = parseTimeWithBaseDate(rangeEndStr, now);
  if (rangeEnd.getTime() < rangeStart.getTime()) {
    rangeEnd.setDate(rangeEnd.getDate() + 1);
  }

  const breaksWithinDuration = [];
  let totalOverlappingBreakTimeMinutes = 0;

  breakTimesArray.forEach((breakItem, index) => {
    if (!breakItem.start || !breakItem.end) {
      return;
    }

    const breakStart = parseTimeWithBaseDate(breakItem.start, rangeStart);
    const breakEnd = parseTimeWithBaseDate(breakItem.end, rangeStart);

    if (breakEnd.getTime() < breakStart.getTime()) {
      breakEnd.setDate(breakEnd.getDate() + 1);
    }

    if (
      breakStart.getTime() < rangeEnd.getTime() &&
      breakEnd.getTime() > rangeStart.getTime()
    ) {
      const overlapStartMs = Math.max(
        breakStart.getTime(),
        rangeStart.getTime()
      );
      const overlapEndMs = Math.min(breakEnd.getTime(), rangeEnd.getTime());

      const overlapDurationMs = overlapEndMs - overlapStartMs;

      if (overlapDurationMs > 0) {
        const overlapDurationMinutes = Math.round(overlapDurationMs / 60000);
        breaksWithinDuration.push({
          ...breakItem,
          overlapDuration: overlapDurationMinutes,
        });
        totalOverlappingBreakTimeMinutes += overlapDurationMinutes;
      }
    }
  });

  return {
    breaksFound: breaksWithinDuration,
    totalOverlappingBreakTimeMinutes,
  };
}

export const shiftPayloadGenerator = (initialValues, values, filter) => {
  let payload;
  const templateType = get(filter, "templateType")
  switch (templateType) {
    case 1:
      payload = {
        uuid: get(initialValues, "template_uuid"),
        shift_uuid: get(initialValues, "id"),
        date: get(filter, "date").format("YYYY-MM-DD"),
        customer_name: get(values, "customer_name", ""),
        copy_to_date: get(values, "copy_to_date", get(filter, "date")).format("YYYY-MM-DD"),
        operator: get(values, "operator", ""),
        supervisor: get(values, "supervisor"),
        wages_per_hour: get(values, "wages_per_hour"),
        manager_uuid: get(values, "manager_uuid"),
        components: map(get(values, "components"), (component) => {
          return {
            component_uuid: get(component, "component_uuid"),
            start_time: get(component, "start_time").format("HH:mm:ss"),
            end_time: get(component, "end_time").format("HH:mm:ss"),
            test_shots: get(component, "test_shots", 0),
            planned_down_time: get(component, "planned_down_time"),
            cycle_time: get(component, "cycle_time"),
            job_id: get(component, "job_id"),
            batch_number: get(component, "batch_number"),
            parts: map(get(component, "parts_full"), (part) => {
              return {
                ...part,
                part_uuid: get(part, "id"),
                meta: {
                  costings: { ...get(part, "meta.costings") },
                  color: get(part, "meta.color", "") ? get(part, "meta.color") : "",
                  cavity: get(part, "meta.cavity"),
                  planned_production_count: get(
                    part,
                    "meta.planned_production_count"
                  ),
                  rejected_count: get(part, "meta.rejected_count", ""),
                  shot_weight: get(part, "meta.shot_weight"),
                  part_weight: get(part, "meta.part_weight"),
                  units_per_box:get(part, "meta.units_per_box",1)
                },
              };
            }),
          };
        }),
      };
      break
    case 2:
      payload = {
        uuid: get(initialValues, "template_uuid"),
        shift_uuid: get(initialValues, "id"),
        date: get(filter, "date").format("YYYY-MM-DD"),
        customer_name: get(values, "customer_name", ""),
        operator: get(values, "operator", ""),
        supervisor: get(values, "supervisor"),
        wages_per_hour: get(values, "wages_per_hour"),
        manager_uuid: get(values, "manager_uuid"),
        copy_to_date: get(values, "copy_to_date", get(filter, "date")).format("YYYY-MM-DD"),
        components: map(get(values, "components"), (component) => {
          return {
            component_uuid: get(component, "component_uuid"),
            start_time: get(component, "start_time").format("HH:mm:ss"),
            end_time: get(component, "end_time").format("HH:mm:ss"),
            test_length: get(component, "test_length", 0),
            planned_down_time: get(component, "planned_down_time"),
            machine_speed: get(component, "machine_speed"),
            job_id: get(component, "job_id"),
            batch_number: get(component, "batch_number"),
            parts: map(get(component, "parts_full"), (part) => {
              return {
                ...part,
                ...get(part, "meta"),
                part_uuid: get(part, "id"),
                meta: {
                  ...get(part, "meta"),
                  costings:{...get(part, "meta.costings")},
                  planned_length: get(
                    part,
                    "meta.planned_length"
                  ),
                  rejected_length: get(part, "meta.rejected_length") || 0,
                  rejected_scrap_type: get(part, "meta.rejected_scrap_type", ""),
                  process: get(part, "meta.process"),
                  part_weight: get(part, "meta.part_weight"),
                  rejected_scrap_weight: get(part, "meta.rejected_scrap_weight") || 0
                },
              };
            }),
          };
        }),
      };
      break
    case 3:
      payload = {
        uuid: get(initialValues, "template_uuid"),
        date: get(filter, "date").format("YYYY-MM-DD"),
        shift_uuid: get(initialValues, "id"),
        copy_to_date: get(values, "copy_to_date", get(filter, "date")).format("YYYY-MM-DD"),
        components: map(get(values, "components"), (component) => {
          return {
            component_uuid: get(component, "component_uuid"),
            start_time: get(component, "start_time").format("HH:mm:ss"),
            end_time: get(component, "end_time").format("HH:mm:ss"),
            test_counts: get(component, "test_counts", 0),
            planned_down_time: get(component, "planned_down_time"),
            job_id: get(component, "job_id"),
            batch_number: get(component, "batch_number"),
            customer_name: get(component, "customer_name", ""),
            operator: get(component, "operator", ""),
            supervisor: get(component, "supervisor"),
            wages_per_hour: get(component, "wages_per_hour"),
            manager_uuid: get(component, "manager_uuid"),
            parts: map(get(component, "parts_full"), (part) => {
              return {
                ...part,
                part_uuid: get(part, "id"),
                meta: {
                  costings: { ...get(part, "meta.costings") },
                  planned_production_count: get(
                    part,
                    "meta.planned_production_count"
                  ),
                  rejected_count: get(part, "meta.rejected_count", ""),
                  part_weight: get(part, "meta.part_weight"),
                  press_cycle: get(part, "meta.press_cycle"),
                  units_per_box:get(part, "meta.units_per_box",1)
                },
              };
            }),
          };
        }),
      };
      break
    case 4:
      payload = {
        uuid: get(initialValues, "template_uuid"),
        shift_uuid: get(initialValues, "id"),
        date: get(filter, "date").format("YYYY-MM-DD"),
        customer_name: get(values, "customer_name", ""),
        operator: get(values, "operator", ""),
        supervisor: get(values, "supervisor"),
        wages_per_hour: get(values, "wages_per_hour"),
        manager_uuid: get(values, "manager_uuid"),
        copy_to_date: get(values, "copy_to_date", get(filter, "date")).format("YYYY-MM-DD"),
        components: map(get(values, "components"), (component) => {
          return {
            component_uuid: get(component, "component_uuid"),
            start_time: get(component, "start_time").format("HH:mm:ss"),
            end_time: get(component, "end_time").format("HH:mm:ss"),
            test_counts: get(component, "test_counts", 0),
            planned_down_time: get(component, "planned_down_time"),
            job_id: get(component, "job_id"),
            batch_number: get(component, "batch_number"),
            cycle_time: get(component, "cycle_time"),
            parts: map(get(component, "parts_full"), (part) => {
              return {
                ...part,
                part_uuid: get(part, "id"),
                meta: {
                  costings: { ...get(part, "meta.costings") },
                  color: get(part, "meta.color", "") ? get(part, "meta.color") : "",
                  machining_cycle: get(part, "meta.machining_cycle"),
                  planned_production: get(
                    part,
                    "meta.planned_production"
                  ),
                  rejected_count: get(part, "meta.rejected_count", ""),
                  raw_weight: get(part, "meta.raw_weight"),
                  finished_weight: get(part, "meta.finished_weight"),
                  units_per_box:get(part, "meta.units_per_box",1)
                },
              };
            }),
          };
        }),
      };
      break
  }
  return payload;
};

export const copyToClipboard = (record) => {
  const { key, ...dataToCopy } = record;
  const textToCopy = JSON.stringify(dataToCopy, null, 2);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success(SHIFT_CONFIG_COPY_SUCCESS);
      })
      .catch((err) => {
        fallbackCopyToClipboard(textToCopy);
      });
  }
};

export const checkIfRecordInClipboard = async () => {
  if (!navigator.clipboard || !navigator.clipboard.readText) {
    message.warning(SHIFT_CONFIG_PASTE_FAILED);
    return null;
  }
  try {
    const clipboardText = await navigator.clipboard.readText();
    if (!clipboardText.trim()) {
      return null;
    }
    try {
      const parsedData = JSON.parse(clipboardText);
      if (
        typeof parsedData === "object" &&
        parsedData !== null &&
        Object.keys(parsedData).length > 0
      ) {
        return parsedData;
      } else {
        return null;
      }
    } catch (jsonError) {
      return null;
    }
  } catch (err) {
    return null;
  }
};

dayjs.extend(customParseFormat);

const toMinutes = (str) => {
  const d = dayjs(str, "HH:mm");
  return d.hour() * 60 + d.minute();
};


const normalizeRange = (startStr, endStr, minStr, maxStr) => {
  const min = toMinutes(minStr);
  const max = toMinutes(maxStr);

  let start = toMinutes(startStr);
  let end = toMinutes(endStr);

  if (start < min) start += 1440;
  if (end <= start) end += 1440;

  return { start, end };
};

export const validateNoComponentsOverlap = (
  fieldListName,
  name,
  minTime,
  maxTime
) => {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      const all = getFieldValue(fieldListName);
      const current = all[name];

      if (!current?.start_time || !current?.end_time) {
        return Promise.resolve();
      }

      const curr = normalizeRange(
        current.start_time,
        current.end_time,
        minTime,
        maxTime
      );

      const overlapFound = all.some((other, idx) => {
        if (idx === name) return false;
        if (!other?.start_time || !other?.end_time) return false;

        const norm = normalizeRange(
          other.start_time,
          other.end_time,
          minTime,
          maxTime
        );

        return curr.start < norm.end && curr.end > norm.start;
      });

      if (overlapFound) {
        return Promise.reject(
          new Error("This break overlaps with another break timing.")
        );
      }

      return Promise.resolve();
    },
  });
};


export const validateStartEndTime = (name) => {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value) return Promise.resolve();
      const allComponents = getFieldValue("components") || [];
      const prevEnd = allComponents?.[name - 1]?.end_time;
      if (prevEnd && !value.isAfter(prevEnd)) {
        return Promise.reject(
          new Error("Start time must be after previous component's end time")
        );
      }
      return Promise.resolve();
    },
  });
};

export const validatePartWeight = (name, rowIndex) => {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      const shotWeight = getFieldValue([
        "components",
        name,
        "parts_full",
        rowIndex,
        "meta",
        "shot_weight",
      ]);

      if (value == null || shotWeight == null) {
        return Promise.resolve();
      }
      if (value > shotWeight) {
        return Promise.reject(
          new Error("Part weight must be less than shot weight")
        );
      }
      return Promise.resolve();
    },
  });
};

export const generateInitialValue = (initialValues) => {
  return {
    ...initialValues,
  };
};

export const initialValuesGenerator = (
  initialValues,
  filter,
  clipboardData,
  start_time,
  end_time,
) => ({
  wages_per_hour: get(initialValues, "default_values.wages_per_hour"),
  manager_uuid: get(initialValues, "config.manager.id", get(initialValues, "manager.id")),
  copy_to_date: !isUndefined(get(initialValues, "copy_to_date")) ? dayjs(get(initialValues, "copy_to_date")).format("DD/MM/YYYY") : get(filter, "date"),
  ...generateInitialValue(get(initialValues, "config")),
  components:
    get(
      generateInitialValue(get(initialValues, "config")),
      "shift_production_components.length"
    ) > 0
      ? map(
        get(
          generateInitialValue(get(initialValues, "config")),
          "shift_production_components"
        ),
        (component) => {
          return {
            ...component,
            ...get(initialValues, "default_values"),
            ...get(component, "component"),
            ...get(component, "meta"),
            component_uuid: get(component, "component.id"),
            start_time: clipboardData
              ? start_time
              : dayjs(get(component, "start_time"), "HH:mm"),
            end_time: clipboardData
              ? end_time
              : dayjs(get(component, "end_time"), "HH:mm"),
            parts: map(get(component, "pc_parts"), (pcPart) =>
              get(pcPart, "part.id")
            ),
            parts_full: map(get(component, "pc_parts"), (pcPart) => {
              return {
                ...get(pcPart, "part"),
                meta: {
                  ...get(pcPart, "part.meta"),
                  ...get(pcPart, "meta"),
                },
              };
            }),
          };
        }
      )
      : [
        {
          ...get(initialValues, "default_values"),
          test_shots: 0,
          start_time: dayjs(get(initialValues, "start"), "HH:mm"),
          end_time: dayjs(get(initialValues, "end"), "HH:mm"),
        },
      ],
});

export const punchPressInitialValuesGenerator = (
  initialValues,
  filter,
  clipboardData,
  start_time,
  end_time,
) => ({
  copy_to_date: !isUndefined(get(initialValues, "copy_to_date")) ? dayjs(get(initialValues, "copy_to_date")).format("DD/MM/YYYY") : get(filter, "date"),
  ...generateInitialValue(get(initialValues, "config")),
  components:
    get(
      generateInitialValue(get(initialValues, "config")),
      "shift_production_components.length"
    ) > 0
      ? map(
        get(
          generateInitialValue(get(initialValues, "config")),
          "shift_production_components"
        ),
        (component) => {
          return {
            ...component,
            ...get(initialValues, "default_values"),
            ...get(component, "component"),
            ...get(component, "meta"),
            customer_name:get(component, "customer_name"),
            operator:get(component, "operator"),
            manager_uuid: get(component, "manager.id", get(initialValues, "config.manager.id", get(initialValues, "manager.id"))),
            component_uuid: get(component, "component.id"),
            start_time: clipboardData
              ? start_time
              : dayjs(get(component, "start_time"), "HH:mm"),
            end_time: clipboardData
              ? end_time
              : dayjs(get(component, "end_time"), "HH:mm"),
            parts: map(get(component, "pc_parts"), (pcPart) =>
              get(pcPart, "part.id")
            ),
            parts_full: map(get(component, "pc_parts"), (pcPart) => {
              return {
                ...get(pcPart, "part"),
                meta: {
                  ...get(pcPart, "part.meta"),
                  ...get(pcPart, "meta"),
                },
              };
            }),
          };
        }
      )
      : [
        {
          ...get(initialValues, "default_values"),
          test_shots: 0,
          start_time: dayjs(get(initialValues, "start"), "HH:mm"),
          end_time: dayjs(get(initialValues, "end"), "HH:mm"),
        },
      ],
});

export function calculateProductionCount(
  start_time,
  end_time,
  planned_down_time = 0,
  cycle_time,
  cavity,
  test_shots = 0
) {
  const startTimeDayjs = dayjs(start_time);
  let endTimeDayjs = dayjs(end_time);

  if (!startTimeDayjs.isValid() || !endTimeDayjs.isValid()) {
    return 0;
  }

  if (endTimeDayjs.isBefore(startTimeDayjs)) {
    endTimeDayjs = endTimeDayjs.add(1, "day");
  }

  if (
    typeof planned_down_time !== "number" ||
    planned_down_time < 0 ||
    typeof cycle_time !== "number" ||
    cycle_time <= 0 ||
    typeof cavity !== "number" ||
    cavity < 0 ||
    typeof test_shots !== "number" ||
    test_shots < 0
  ) {
    return 0;
  }

  const totalProductionTimeInSeconds = endTimeDayjs.diff(
    startTimeDayjs,
    "second"
  );

  const plannedDownTimeInSeconds = planned_down_time * 60;

  const effectiveRunTimeInSeconds =
    totalProductionTimeInSeconds - plannedDownTimeInSeconds;

  if (effectiveRunTimeInSeconds <= 0) {
    return 0;
  }

  const totalPossibleCycles = Math.floor(
    effectiveRunTimeInSeconds / cycle_time
  );

  const effectiveProductionCycles = Math.max(
    0,
    totalPossibleCycles - test_shots
  );

  const totalProductionCount = effectiveProductionCycles * cavity;

  return totalProductionCount;
}

export function calculateExtrutionProductionCount(
  start_time,
  end_time,
  planned_down_time = 0,
  speed,
  test_length = 0
) {
  const startTimeDayjs = dayjs(start_time);
  let endTimeDayjs = dayjs(end_time);

  if (!startTimeDayjs.isValid() || !endTimeDayjs.isValid()) {
    return 0;
  }

  if (endTimeDayjs.isBefore(startTimeDayjs)) {
    endTimeDayjs = endTimeDayjs.add(1, "day");
  }

  if (
    typeof planned_down_time !== "number" ||
    planned_down_time < 0 ||
    typeof speed !== "number"
  ) {
    return 0;
  }

  const totalProductionTimeInMinutes = endTimeDayjs.diff(
    startTimeDayjs,
    "minute"
  );

  const effectiveRunTimeInMinutes =
    totalProductionTimeInMinutes - planned_down_time;

  if (effectiveRunTimeInMinutes <= 0) {
    return 0;
  }

  const totalProductionCount = effectiveRunTimeInMinutes * speed;

  return totalProductionCount - test_length;
}

export function calculatePunchPressProductionCount(
  start_time,
  end_time,
  planned_down_time = 0,
  test_shots = 0
) {
  const startTimeDayjs = dayjs(start_time);
  let endTimeDayjs = dayjs(end_time);

  if (!startTimeDayjs.isValid() || !endTimeDayjs.isValid()) {
    return 0;
  }

  if (endTimeDayjs.isBefore(startTimeDayjs)) {
    endTimeDayjs = endTimeDayjs.add(1, "day");
  }

  if (
    typeof planned_down_time !== "number" ||
    planned_down_time < 0 ||
    typeof cycle_time !== "number" ||
    cycle_time <= 0 ||
    typeof test_shots !== "number" ||
    test_shots < 0
  ) {
    return 0;
  }

  const totalProductionTimeInSeconds = endTimeDayjs.diff(
    startTimeDayjs,
    "second"
  );

  const plannedDownTimeInSeconds = planned_down_time * 60;

  const effectiveRunTimeInSeconds =
    totalProductionTimeInSeconds - plannedDownTimeInSeconds;

  if (effectiveRunTimeInSeconds <= 0) {
    return 0;
  }

  const totalPossibleCycles = Math.floor(
    effectiveRunTimeInSeconds / cycle_time
  );

  const effectiveProductionCycles = Math.max(
    0,
    totalPossibleCycles - test_shots
  );

  const totalProductionCount = effectiveProductionCycles

  return totalProductionCount;
}
export function calculatCNCProductionCount(
  start_time,
  end_time,
  planned_down_time = 0,
  cycle_time,
  test_shots = 0
) {
  const startTimeDayjs = dayjs(start_time);
  let endTimeDayjs = dayjs(end_time);

  if (!startTimeDayjs.isValid() || !endTimeDayjs.isValid()) {
    return 0;
  }

  if (endTimeDayjs.isBefore(startTimeDayjs)) {
    endTimeDayjs = endTimeDayjs.add(1, "day");
  }

  if (
    typeof planned_down_time !== "number" ||
    planned_down_time < 0 ||
    typeof cycle_time !== "number" ||
    cycle_time <= 0 ||
    typeof test_shots !== "number" ||
    test_shots < 0
  ) {
    return 0;
  }

  const totalProductionTimeInSeconds = endTimeDayjs.diff(
    startTimeDayjs,
    "second"
  );

  const plannedDownTimeInSeconds = planned_down_time * 60;

  const effectiveRunTimeInSeconds =
    totalProductionTimeInSeconds - plannedDownTimeInSeconds;

  if (effectiveRunTimeInSeconds <= 0) {
    return 0;
  }

  const totalPossibleCycles = Math.floor(
    effectiveRunTimeInSeconds / cycle_time
  );

  const effectiveProductionCycles = Math.max(
    0,
    totalPossibleCycles - test_shots
  );

  const totalProductionCount = effectiveProductionCycles

  return totalProductionCount;
}

export const plannedProductionHandler = (type, name, part, form) => {
  const componentValues = form.getFieldValue(["components", name]) || {};
  const {
    start_time,
    end_time,
    planned_down_time = 0,
    test_shots = 0,
    machine_speed,
    test_length,
  } = componentValues;
  const cycle_time = get(part, 'meta.cycle_time')

  const commonArgs = [start_time, end_time, planned_down_time];

  let plannedObj = {};

  switch (type) {
    case 1: {
      const cavity = get(part, "meta.cavity", 0);
      const planned_production_count = calculateProductionCount(
        ...commonArgs,
        cycle_time,
        cavity,
        test_shots
      );
      plannedObj.planned_production_count = planned_production_count;
      break;
    }

    case 2: {
      const planned_length = calculateExtrutionProductionCount(
        ...commonArgs,
        machine_speed,
        test_length
      );
      plannedObj.planned_length = planned_length;
      break;
    }

    case 3: {
      plannedObj.planned_production_count = 0;
      break;
    }

    case 4: {
      const cncCount = calculatCNCProductionCount(
        ...commonArgs,
        cycle_time,
        test_shots
      );
      plannedObj.planned_production = cncCount;
      break;
    }

    default:
      plannedObj = {};
  }
  return plannedObj;
};
